import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { PiTimerBold } from 'react-icons/pi';
import { Button } from '@v2/design-system';
import RequirementFieldRenderer from '@v2/components/forms/RequirementFieldRenderer';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import ConferenceSheetImportButton from './ConferenceSheetImportButton';
import type { ShipmentTaskRow, TaskComment, TaskFormPayload } from './types';
import { SHIPMENT_STAGES } from './types';

type ModalKind = 'hooks' | 'detail' | 'comments' | 'complete' | 'edit' | 'create' | null;

type Props = {
    shipmentId: number;
    task: ShipmentTaskRow | null;
    kind: ModalKind;
    onClose: () => void;
    onUpdated: () => void;
};

export default function TaskModals({ shipmentId, task, kind, onClose, onUpdated }: Props) {
    if (!kind) return null;

    if (kind === 'create') {
        return <TaskFormModal shipmentId={shipmentId} task={null} onClose={onClose} onUpdated={onUpdated} />;
    }
    if (!task) return null;

    if (kind === 'edit') {
        return <TaskFormModal shipmentId={shipmentId} task={task} onClose={onClose} onUpdated={onUpdated} />;
    }
    if (kind === 'hooks') {
        return <TaskHooksModal task={task} onClose={onClose} />;
    }
    if (kind === 'detail') {
        return <TaskDetailModal shipmentId={shipmentId} task={task} onClose={onClose} onUpdated={onUpdated} />;
    }
    if (kind === 'comments') {
        return <TaskCommentsModal shipmentId={shipmentId} task={task} onClose={onClose} onUpdated={onUpdated} />;
    }
    return <TaskCompleteModal shipmentId={shipmentId} task={task} onClose={onClose} onUpdated={onUpdated} />;
}

function TaskFormModal({
    shipmentId,
    task,
    onClose,
    onUpdated,
}: {
    shipmentId: number;
    task: ShipmentTaskRow | null;
    onClose: () => void;
    onUpdated: () => void;
}) {
    const isEdit = Boolean(task);
    const { register, handleSubmit } = useForm<TaskFormPayload>({
        defaultValues: {
            stage: task?.stage ?? 'pedido',
            title: task?.title ?? '',
            description: task?.description ?? '',
            responsible: task?.responsible ?? '',
            notes: task?.notes ?? '',
            hooks_guide: task?.hooks_guide ?? '',
        },
    });

    const mutation = useMutation({
        mutationFn: async (values: TaskFormPayload) => {
            if (isEdit && task) {
                await api.patch(`/shipments/${shipmentId}/tasks/${task.id}`, values);
            } else {
                await api.post(`/shipments/${shipmentId}/tasks`, values);
            }
        },
        onSuccess: () => {
            toast.success(isEdit ? 'Tarefa atualizada' : 'Tarefa criada');
            onUpdated();
            onClose();
        },
    });

    return (
        <V2Modal
            open
            title={isEdit ? 'Editar tarefa' : 'Nova tarefa'}
            onClose={onClose}
            size="lg"
            footer={
                <>
                    <Button variant="outline" size="sm" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button size="sm" isLoading={mutation.isPending} onClick={handleSubmit((v) => mutation.mutate(v))}>
                        Salvar
                    </Button>
                </>
            }
        >
            <div className="space-y-3">
                <label className="block text-sm">
                    <span className="mb-1 block font-medium text-gray-700">Estágio</span>
                    <select {...register('stage')} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
                        {SHIPMENT_STAGES.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="block text-sm">
                    <span className="mb-1 block font-medium text-gray-700">Título</span>
                    <input {...register('title', { required: true })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
                </label>
                <label className="block text-sm">
                    <span className="mb-1 block font-medium text-gray-700">Setor</span>
                    <input {...register('responsible')} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
                </label>
                <label className="block text-sm">
                    <span className="mb-1 block font-medium text-gray-700">Descrição</span>
                    <textarea {...register('description')} rows={2} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
                </label>
                <label className="block text-sm">
                    <span className="mb-1 block font-medium text-gray-700">Observações</span>
                    <textarea {...register('notes')} rows={2} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
                </label>
            </div>
        </V2Modal>
    );
}

const SLA_BOX: Record<string, string> = {
    overdue: 'border-rose-200 bg-rose-50 text-rose-800',
    due_today: 'border-amber-200 bg-amber-50 text-amber-900',
    due_soon: 'border-amber-200 bg-amber-50 text-amber-800',
    on_time: 'border-gray-200 bg-gray-50 text-gray-700',
    waiting: 'border-gray-200 bg-gray-50 text-gray-600',
    done_on_time: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    done_late: 'border-rose-200 bg-rose-50/60 text-rose-700',
};

/** Prazo (SLA) da tarefa — configurado em Configurações → Prazos das tarefas. */
function TaskSlaBox({ task }: { task: ShipmentTaskRow }) {
    if (!task.sla_days) {
        return (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-dashed border-gray-200 px-3 py-2 text-xs text-gray-500">
                <PiTimerBold className="size-4 shrink-0" />
                Sem prazo (SLA) definido para esta tarefa.
                <a href="/v2/settings/task-sla" className="ml-auto font-semibold text-primary hover:underline">
                    Definir prazos
                </a>
            </div>
        );
    }
    const st = task.sla_status;
    const due = task.due_date ? new Date(task.due_date + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' }) : null;

    return (
        <div className={`mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border px-3 py-2 text-sm ${SLA_BOX[st?.state ?? 'on_time'] ?? SLA_BOX.on_time}`}>
            <span className="inline-flex items-center gap-1.5 font-semibold">
                <PiTimerBold className="size-4 shrink-0" />
                Prazo: {task.sla_days} dia(s) {task.sla_business_days === false ? 'corridos' : 'úteis'}
            </span>
            {task.sla_start_label && <span className="text-xs opacity-80">{task.sla_start_label}</span>}
            <span className="ml-auto text-xs font-semibold">
                {st?.state === 'waiting'
                    ? 'Ainda não começou a contar'
                    : [due ? `Vence ${due}` : null, st?.label && st.state !== 'on_time' ? st.label : null].filter(Boolean).join(' · ')}
            </span>
        </div>
    );
}

function TaskHooksModal({ task, onClose }: { task: ShipmentTaskRow; onClose: () => void }) {
    const hasGuide = Boolean(task.resolved_hooks_guide_html || task.resolved_hooks_guide);
    return (
        <V2Modal open title="Como fazer" description={task.title} onClose={onClose} size="5xl">
            <TaskSlaBox task={task} />
            {task.resolved_hooks_guide_html ? (
                <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: task.resolved_hooks_guide_html }} />
            ) : hasGuide ? (
                <pre className="whitespace-pre-wrap text-sm text-gray-700">{task.resolved_hooks_guide}</pre>
            ) : (
                <p className="text-sm text-gray-500">Sem passo a passo cadastrado para esta tarefa.</p>
            )}
        </V2Modal>
    );
}

function TaskDetailModal({
    shipmentId,
    task,
    onClose,
    onUpdated,
}: {
    shipmentId: number;
    task: ShipmentTaskRow;
    onClose: () => void;
    onUpdated: () => void;
}) {
    const showDocuments = task.title.includes('Conferir itens e quantidades');
    const { data: context } = useQuery({
        queryKey: ['task-context', shipmentId, task.id],
        enabled: showDocuments,
        queryFn: async () => {
            const { data } = await api.get<{ documents: Array<{ name?: string; url?: string }> }>(
                `/shipments/${shipmentId}/tasks/${task.id}/context`,
            );
            return data;
        },
    });

    return (
        <V2Modal open title={task.title} onClose={onClose} size="5xl">
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
                <DetailItem label="Estágio" value={task.stage_label} />
                <DetailItem label="Setor" value={task.responsible || '—'} />
                <DetailItem label="Modo" value={task.is_auto ? 'Automático' : 'Manual'} />
                <DetailItem label="Atribuído" value={task.assigned_user?.name || '—'} />
                <DetailItem label="Status" value={task.is_completed ? 'Concluída' : 'Pendente'} />
                <DetailItem label="Concluída em" value={task.completed_at ? new Date(task.completed_at).toLocaleString('pt-BR') : '—'} />
                <DetailItem label="Executado por" value={task.completed_by_user?.name || '—'} />
            </dl>
            {task.description && (
                <div className="mt-4">
                    <p className="text-xs font-semibold uppercase text-gray-500">Descrição</p>
                    <p className="mt-1 text-sm text-gray-700">{task.description}</p>
                </div>
            )}
            {task.notes && (
                <div className="mt-4">
                    <p className="text-xs font-semibold uppercase text-gray-500">Observações</p>
                    <p className="mt-1 text-sm text-gray-700">{task.notes}</p>
                </div>
            )}
            {task.actions.can_import_conference_sheet && (
                <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-xs font-semibold uppercase text-gray-500">Planilha Details</p>
                    <p className="mt-1 text-sm text-gray-600">
                        Importe o Excel do fornecedor (uma aba por container).{' '}
                        {task.checks_summary && task.checks_summary.containers > 0
                            ? `${task.checks_summary.containers} container(s), ${task.checks_summary.items} item(ns) já importados.`
                            : 'Nenhum dado importado ainda.'}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <ConferenceSheetImportButton shipmentId={shipmentId} onSuccess={onUpdated} />
                        {task.actions.conference_url_after_import && task.checks_summary && task.checks_summary.containers > 0 ? (
                            <a
                                href={task.actions.conference_url_after_import}
                                className="text-sm text-primary hover:underline"
                            >
                                Abrir romaneio
                            </a>
                        ) : null}
                    </div>
                </div>
            )}
            {showDocuments && (
                <div className="mt-4">
                    <p className="text-xs font-semibold uppercase text-gray-500">Documentos (Ploomes)</p>
                    {!context?.documents?.length ? (
                        <p className="mt-1 text-sm text-gray-500">Nenhum documento disponível.</p>
                    ) : (
                        <ul className="mt-2 space-y-1">
                            {context.documents.map((doc, i) => (
                                <li key={i}>
                                    {doc.url ? (
                                        <a href={doc.url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">
                                            {doc.name || doc.url}
                                        </a>
                                    ) : (
                                        <span className="text-sm text-gray-700">{doc.name || '—'}</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </V2Modal>
    );
}

function DetailItem({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <dt className="text-xs text-gray-500">{label}</dt>
            <dd className="font-medium text-gray-900">{value}</dd>
        </div>
    );
}

function TaskCommentsModal({
    shipmentId,
    task,
    onClose,
    onUpdated,
}: {
    shipmentId: number;
    task: ShipmentTaskRow;
    onClose: () => void;
    onUpdated: () => void;
}) {
    const [body, setBody] = useState('');
    const qc = useQueryClient();
    const key = ['task-comments', shipmentId, task.id];

    const { data: comments = [], isLoading } = useQuery({
        queryKey: key,
        queryFn: async () => {
            const { data } = await api.get<{ data: TaskComment[] }>(`/shipments/${shipmentId}/tasks/${task.id}/comments`);
            return data.data;
        },
    });

    const postComment = useMutation({
        mutationFn: async () => {
            await api.post(`/shipments/${shipmentId}/tasks/${task.id}/comments`, { body });
        },
        onSuccess: () => {
            setBody('');
            qc.invalidateQueries({ queryKey: key });
            onUpdated();
            toast.success('Comentário adicionado');
        },
    });

    return (
        <V2Modal
            open
            title="Comentários"
            description={task.title}
            onClose={onClose}
            size="2xl"
            footer={
                <>
                    <Button variant="outline" size="sm" onClick={onClose}>
                        Fechar
                    </Button>
                    <Button size="sm" isLoading={postComment.isPending} onClick={() => postComment.mutate()} disabled={!body.trim()}>
                        Enviar
                    </Button>
                </>
            }
        >
            <div className="mb-4">
                <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={3}
                    placeholder="Escreva um comentário…"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
            </div>
            {isLoading ? (
                <p className="text-sm text-gray-500">Carregando…</p>
            ) : comments.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhum comentário ainda.</p>
            ) : (
                <ul className="space-y-3">
                    {comments.map((c) => (
                        <CommentItem key={c.id} comment={c} />
                    ))}
                </ul>
            )}
        </V2Modal>
    );
}

function CommentItem({ comment }: { comment: TaskComment }) {
    return (
        <li className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm">
            <p className="font-medium text-gray-900">{comment.author?.name || '—'}</p>
            <p className="text-xs text-gray-500">{comment.created_at ? new Date(comment.created_at).toLocaleString('pt-BR') : ''}</p>
            <p className="mt-1 text-gray-700">{comment.body}</p>
            {comment.replies?.map((r) => (
                <div key={r.id} className="mt-2 ms-4 border-s-2 border-gray-200 ps-3">
                    <CommentItem comment={r} />
                </div>
            ))}
        </li>
    );
}

function TaskCompleteModal({
    shipmentId,
    task,
    onClose,
    onUpdated,
}: {
    shipmentId: number;
    task: ShipmentTaskRow;
    onClose: () => void;
    onUpdated: () => void;
}) {
    const isReopen = task.is_completed;
    const { register, handleSubmit, setValue } = useForm<Record<string, unknown>>({
        defaultValues: task.requirement_defaults ?? {},
    });

    const fetchRate = useMutation({
        mutationFn: async () => {
            const { data } = await api.get<{ rate: number; date: string; quoted_at: string }>('/tasks/exchange-rate');
            return data;
        },
        onSuccess: (data) => {
            // Mesmas chaves do requirement da tarefa (TaskTemplate "Cotação de câmbio").
            setValue('taxa_cambio', data.rate, { shouldDirty: true });
            setValue('data_cotacao', data.date, { shouldDirty: true });
            toast.success(`Cotação USD/BRL ${data.rate.toLocaleString('pt-BR', { minimumFractionDigits: 4 })} (${data.quoted_at})`);
        },
        onError: (err: { response?: { data?: { message?: string } } }) => {
            toast.error(err?.response?.data?.message ?? 'Não foi possível buscar a cotação.');
        },
    });

    const mutation = useMutation({
        mutationFn: async (values: Record<string, unknown>) => {
            if (isReopen) {
                await api.post(`/shipments/${shipmentId}/tasks/${task.id}/reopen`);
            } else {
                await api.post(`/shipments/${shipmentId}/tasks/${task.id}/complete`, {
                    requirement_data: task.has_requirements ? values : undefined,
                });
            }
        },
        onSuccess: () => {
            toast.success(isReopen ? 'Tarefa reaberta' : 'Tarefa concluída');
            onUpdated();
            onClose();
        },
    });

    const schema = task.requirements_schema ?? [];
    const isExchangeTask = task.title.toLowerCase().includes('cotação');

    return (
        <V2Modal
            open
            title={isReopen ? 'Reabrir tarefa' : 'Concluir tarefa'}
            description={task.title}
            onClose={onClose}
            size={schema.length && !isReopen ? '2xl' : 'md'}
            footer={
                <>
                    <Button variant="outline" size="sm" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button
                        size="sm"
                        color={isReopen ? 'warning' : 'primary'}
                        isLoading={mutation.isPending}
                        onClick={handleSubmit((v) => mutation.mutate(v))}
                    >
                        {isReopen ? 'Reabrir' : 'Concluir'}
                    </Button>
                </>
            }
        >
            {task.actions.can_import_conference_sheet && !isReopen && (
                <div className="mb-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3">
                    <p className="mb-2 text-xs text-gray-600">
                        Planilha Details do fornecedor (opcional nesta etapa):
                    </p>
                    <ConferenceSheetImportButton shipmentId={shipmentId} onSuccess={onUpdated} />
                </div>
            )}
            {!isReopen && isExchangeTask && (
                <div className="mb-4">
                    <Button type="button" size="sm" variant="outline" isLoading={fetchRate.isPending} onClick={() => fetchRate.mutate()}>
                        Buscar cotação USD/BRL
                    </Button>
                </div>
            )}
            {!isReopen && schema.length > 0 ? (
                <RequirementFieldRenderer schema={schema} register={register} setValue={setValue} />
            ) : (
                <p className="text-sm text-gray-600">
                    {isReopen ? 'Deseja reabrir esta tarefa?' : 'Confirmar conclusão desta tarefa?'}
                </p>
            )}
        </V2Modal>
    );
}
